
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabase } from "@/integrations/supabase/client";
import { LensInventory, LensFormValues, lensSchema, CameraInventory } from "@/types/inventory";
import { toast } from "sonner";

export function useLensDetails(id: string | undefined) {
  const [lens, setLens] = useState<LensInventory | null>(null);
  const [compatibleCameras, setCompatibleCameras] = useState<CameraInventory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const form = useForm<LensFormValues>({
    resolver: zodResolver(lensSchema),
    defaultValues: {
      brand: '',
      lens_name: '',
      mount: '',
      status: 'working',
      serial_number: '',
      purchase_date: '',
      purchase_price: '',
      notes: '',
    }
  });

  useEffect(() => {
    if (id) {
      fetchLensDetails();
    }
  }, [id]);

  useEffect(() => {
    if (lens) {
      form.reset({
        brand: lens.brand,
        lens_name: lens.lens_name,
        mount: lens.mount,
        status: lens.status,
        serial_number: lens.serial_number || '',
        purchase_date: lens.purchase_date || '',
        purchase_price: lens.purchase_price ? lens.purchase_price.toString() : '',
        notes: lens.notes || '',
      });
    }
  }, [lens, form]);

  const fetchLensDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch lens details
      const { data: lensData, error: lensError } = await supabase
        .from('lens_inventory')
        .select('*')
        .eq('id', id)
        .single();

      if (lensError) throw lensError;
      if (!lensData) throw new Error("Lens not found");
      
      const typedLensData = lensData as unknown as LensInventory;
      setLens(typedLensData);
      
      // Fetch compatible cameras - cameras with matching mount or adapter mounts
      const { data: cameraData, error: cameraError } = await supabase
        .from('camera_inventory')
        .select('*')
        .or(`mount.eq.${typedLensData.mount},adapter_mounts.ilike.%${typedLensData.mount}%`);

      if (cameraError) throw cameraError;
      
      if (Array.isArray(cameraData) && cameraData.length > 0) {
        // Filter out cameras that are point and shoot
        const filteredCameras = cameraData.filter((camera) => 
          camera.camera_type !== 'point_and_shoot'
        );
        setCompatibleCameras(filteredCameras as CameraInventory[]);
      } else {
        setCompatibleCameras([]);
      }
    } catch (error: any) {
      console.error("Error fetching lens details:", error);
      setError(error.message || "Failed to load lens details");
      toast.error("Failed to load lens details");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateLens = async (values: LensFormValues) => {
    try {
      if (!lens) return;
      
      const { error } = await supabase
        .from('lens_inventory')
        .update({
          brand: values.brand,
          lens_name: values.lens_name,
          mount: values.mount,
          status: values.status,
          serial_number: values.serial_number || null,
          purchase_date: values.purchase_date || null,
          purchase_price: values.purchase_price ? parseFloat(values.purchase_price) : null,
          notes: values.notes || null,
        })
        .eq('id', lens.id);
        
      if (error) throw error;
      
      setIsEditDialogOpen(false);
      toast.success('Lens updated successfully');
      
      setLens({
        ...lens,
        brand: values.brand,
        lens_name: values.lens_name,
        mount: values.mount,
        status: values.status,
        serial_number: values.serial_number || null,
        purchase_date: values.purchase_date || null,
        purchase_price: values.purchase_price ? parseFloat(values.purchase_price) : null,
        notes: values.notes || null,
      });
      
      fetchLensDetails();
    } catch (err: any) {
      console.error("Error updating lens:", err);
      toast.error("Failed to update lens");
    }
  };

  return {
    lens,
    compatibleCameras,
    loading,
    error,
    isEditDialogOpen,
    setIsEditDialogOpen,
    form,
    handleUpdateLens,
    fetchLensDetails
  };
}
